import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { StandardProps } from '../../types/common';

import styles from './GlobalHeader.module.scss';

interface GlobalHeaderProps extends StandardProps {
  /**
   * Html tag to use
   */
  tag?: 'header' | 'div';

  /**
   * Determines whether the header is sticky
   */
  sticky?: boolean;
}

export const GlobalHeader = ({
  children,
  tag: Tag = 'header',
  className,
  style,
  sticky = false,
  testid = 'global-header',
}: GlobalHeaderProps): JSX.Element => {
  const [isTabbing, setIsTabbing] = useState(false);
  const [isLastChild, setIsLastChild] = useState(false);
  const headerElRef: React.RefObject<HTMLDivElement> = useRef();
  const skipToElRef: React.RefObject<HTMLParagraphElement> = useRef();

  useEffect(() => {
    const handleIsTabbing = (e: KeyboardEvent) => {
      e.key === 'Tab' && setIsTabbing(true);
    };
    const handleIsNotTabbing = () => {
      setIsTabbing(false);
    };
    document?.addEventListener('keydown', handleIsTabbing);
    document?.addEventListener('mousedown', handleIsNotTabbing);
    return () => {
      document?.removeEventListener('keydown', handleIsTabbing);
      document?.removeEventListener('mousedown', handleIsNotTabbing);
    };
  }, []);

  const skipHeader = () => {
    let focusEl: HTMLElement;
    let currentEl = headerElRef.current.nextSibling as HTMLElement;

    while (!focusEl) {
      // Find focusable html element sibling to the header tag
      if (currentEl?.nodeType === 1 && currentEl?.tagName !== 'SCRIPT') {
        focusEl = currentEl;
      }
      // Non-focusable element, continue with next sibling
      else if (currentEl?.nextSibling) {
        currentEl = currentEl.nextSibling as HTMLElement;
      }
      // Could not find a focusable element as a sibling, render a new paragraph
      else {
        setIsLastChild(true);

        const setFocus = () => {
          if (document?.activeElement === skipToElRef.current) {
            clearInterval(setFocusInterval);
          }

          if (skipToElRef.current) {
            skipToElRef.current.focus();
          }
        };

        const setFocusInterval = setInterval(setFocus, 10);

        break;
      }
    }

    if (focusEl) {
      focusEl.setAttribute('tabIndex', '-1');
      focusEl.focus();
    }
  };

  return (
    <Tag
      className={clsx(
        // TODO: these classes needs to be prefixed with 'hds-' in the design system
        styles['global-header'],
        {
          [styles['global-header_sticky']]: sticky,
          [styles['is-tabbing']]: isTabbing,
        },
        className
      )}
      ref={headerElRef}
      style={style}
      data-testid={testid}
    >
      <button
        className={clsx('global-header__skip', styles['sr-only'])}
        type='button'
        onClick={skipHeader}
      >
        Skip to Main Content
      </button>
      {children}
      {isLastChild && (
        <p className={styles['sr-only']} tabIndex={-1} ref={skipToElRef}>
          Bottom of the header
        </p>
      )}
    </Tag>
  );
};
